import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Slider } from '../ui/slider';
import { cn } from '../../lib/utils';
import { Pipette, RefreshCw } from 'lucide-react';

const presetColors = [
  // Blues
  '#1a365d', '#1e40af', '#2563eb', '#3b82f6', '#60a5fa',
  // Purples
  '#5b21b6', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd',
  // Pinks
  '#be185d', '#db2777', '#ec4899', '#f472b6', '#f9a8d4',
  // Reds
  '#991b1b', '#dc2626', '#ef4444', '#f87171', '#fca5a5',
  // Oranges
  '#c2410c', '#ea580c', '#f97316', '#fb923c', '#fdba74',
  // Yellows
  '#a16207', '#ca8a04', '#eab308', '#facc15', '#fde047',
  // Greens
  '#166534', '#16a34a', '#22c55e', '#4ade80', '#86efac',
  // Teals
  '#0f766e', '#0d9488', '#14b8a6', '#2dd4bf', '#00d4aa',
  // Cyans
  '#0e7490', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9',
  // Neutrals
  '#0f172a', '#1e293b', '#475569', '#94a3b8', '#f1f5f9',
];

// Convert HEX to HSL
const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Convert HSL to HEX
const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const ColorPicker = ({ 
  color, 
  onChange, 
  label,
  showInput = true,
  showLabel = true,
  className 
}) => {
  const [inputValue, setInputValue] = useState(color);
  const [isOpen, setIsOpen] = useState(false);
  const [hsl, setHsl] = useState(() => hexToHsl(color));
  const [activeTab, setActiveTab] = useState('presets');

  useEffect(() => {
    setInputValue(color);
    setHsl(hexToHsl(color));
  }, [color]);

  const handleInputChange = (e) => {
    let value = e.target.value;
    setInputValue(value);
    
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      onChange(value);
      setHsl(hexToHsl(value));
    }
  };

  const handlePresetClick = (preset) => {
    setInputValue(preset);
    onChange(preset);
    setHsl(hexToHsl(preset));
  };

  const handleNativeColorChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    onChange(value);
    setHsl(hexToHsl(value));
  };

  const handleHslChange = useCallback((type, value) => {
    const newHsl = { ...hsl, [type]: value };
    setHsl(newHsl);
    const newHex = hslToHex(newHsl.h, newHsl.s, newHsl.l);
    setInputValue(newHex);
    onChange(newHex);
  }, [hsl, onChange]);

  return (
    <div className={cn("space-y-2", className)}>
      {showLabel && label && <Label className="text-[#1a365d] font-medium">{label}</Label>}
      
      <div className="flex items-center gap-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              className="w-12 h-12 rounded-xl border-2 border-[#e2e8f0] shadow-sm hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group"
              style={{ backgroundColor: color }}
              data-testid="color-picker-trigger"
            >
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <Pipette className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('presets')}
                className={cn(
                  "flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeTab === 'presets' 
                    ? "bg-[#1a365d] text-white" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                Préréglages
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={cn(
                  "flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeTab === 'custom' 
                    ? "bg-[#1a365d] text-white" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                Personnalisé
              </button>
            </div>

            {activeTab === 'presets' && (
              <div className="grid grid-cols-5 gap-2 mb-4">
                {presetColors.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      "w-full aspect-square rounded-lg border-2 transition-all hover:scale-110 hover:shadow-md",
                      color === preset ? "border-[#00d4aa] ring-2 ring-[#00d4aa]/30 scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: preset }}
                    title={preset}
                  />
                ))}
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="space-y-4 mb-4">
                {/* Native color picker */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="color"
                      value={color}
                      onChange={handleNativeColorChange}
                      className="w-16 h-16 rounded-xl cursor-pointer border-2 border-[#e2e8f0]"
                      style={{ padding: 0 }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Cliquez pour ouvrir le sélecteur</p>
                    <div 
                      className="w-full h-8 rounded-lg border border-[#e2e8f0]"
                      style={{ backgroundColor: color }}
                    />
                  </div>
                </div>

                {/* HSL Sliders */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs text-gray-600">Teinte (H)</Label>
                      <span className="text-xs font-mono text-gray-500">{hsl.h}°</span>
                    </div>
                    <div 
                      className="h-3 rounded-full"
                      style={{
                        background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                      }}
                    >
                      <Slider
                        value={[hsl.h]}
                        min={0}
                        max={360}
                        step={1}
                        onValueChange={(v) => handleHslChange('h', v[0])}
                        className="relative top-[-2px]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs text-gray-600">Saturation (S)</Label>
                      <span className="text-xs font-mono text-gray-500">{hsl.s}%</span>
                    </div>
                    <div 
                      className="h-3 rounded-full"
                      style={{
                        background: `linear-gradient(to right, ${hslToHex(hsl.h, 0, hsl.l)}, ${hslToHex(hsl.h, 100, hsl.l)})`
                      }}
                    >
                      <Slider
                        value={[hsl.s]}
                        min={0}
                        max={100}
                        step={1}
                        onValueChange={(v) => handleHslChange('s', v[0])}
                        className="relative top-[-2px]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs text-gray-600">Luminosité (L)</Label>
                      <span className="text-xs font-mono text-gray-500">{hsl.l}%</span>
                    </div>
                    <div 
                      className="h-3 rounded-full"
                      style={{
                        background: `linear-gradient(to right, #000000, ${hslToHex(hsl.h, hsl.s, 50)}, #ffffff)`
                      }}
                    >
                      <Slider
                        value={[hsl.l]}
                        min={0}
                        max={100}
                        step={1}
                        onValueChange={(v) => handleHslChange('l', v[0])}
                        className="relative top-[-2px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Hex input with preview */}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
              <div 
                className="w-10 h-10 rounded-lg border border-[#e2e8f0] flex-shrink-0"
                style={{ backgroundColor: inputValue }}
              />
              <Input
                value={inputValue}
                onChange={handleInputChange}
                placeholder="#000000"
                className="font-mono text-sm"
                maxLength={7}
              />
              <button
                onClick={() => handlePresetClick('#1a365d')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Réinitialiser"
              >
                <RefreshCw className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </PopoverContent>
        </Popover>
        
        {showInput && (
          <Input
            value={inputValue}
            onChange={handleInputChange}
            placeholder="#000000"
            className="font-mono text-sm w-28"
            maxLength={7}
            data-testid="color-hex-input"
          />
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
